import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ImageDto } from 'src/app/models/image-dto';
import { MultiLangStrDto } from 'src/app/models/multi-lang-str-dto';

@Component({
  selector: 'app-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrls: ['./image-gallery.component.scss']
})
export class ImageGalleryComponent implements OnInit {
  @Input() images: ImageDto[] = [];
  @Input() thumbnails: ImageDto[] = [];
  @Input() objectName: string = '';
  @Input() customError: string | null = null;
  // Off when the description field holds something that doesn't describe the image itself.
  @Input() altFromDescription: boolean = true;
  @Input() imageDetails = [
    { label: new MultiLangStrDto('שם הצלם: ','Name of the photographer: '), property: 'photographerName' },
    { label: new MultiLangStrDto('תיאור: ','Description: '), property: 'imageDescription' },
    { label: new MultiLangStrDto('זוית צילום: ','Photo angle: '), property: 'imageAngle' },
    { label: new MultiLangStrDto('B Number: ','B Number: '), property: 'archivesImageId' },
  ];

  displayCustom: boolean = false;
  activeIndex: number = 0;
  zoomScale: number = 1;
  zoomLevel: string = `scale(${this.zoomScale})`;
  imageLoadingStates: { [index: number]: 'loading' | 'loaded' | 'error' | 'unsupported' } = {};
  imageErrorMessages: { [index: number]: string } = {};

  responsiveOptions: any[] = [
    {
        breakpoint: '1500px',
        numVisible: 5
    },
    {
        breakpoint: '1024px',
        numVisible: 3
    },
    {
        breakpoint: '768px',
        numVisible: 2
    },
    {
        breakpoint: '560px',
        numVisible: 1
    }
];
  itemsPerPage: number = 1;

  get imageIndices(): number[] {
    const pageCount = Math.ceil(this.images.length / this.itemsPerPage);
    return Array.from({ length: pageCount }, (_, index) => index);
  }
ngOnInit(){
  if (!this.thumbnails || this.thumbnails.length === 0) {
    this.thumbnails = this.images;
  }
}
  firstImage(): void {
    this.activeIndex = 0;
  }

  prevImage(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  nextImage(): void {
    if (this.activeIndex < this.images.length - 1) {
      this.activeIndex++;
    }
  }
  // The image's own description says the most; without one, the caller's name for it.
  // The position is only worth reading out when there's more than one image.
  getImageAltText(): string {
    const description = this.altFromDescription && this.images[this.activeIndex]?.imageDescription?.trim();
    if (description) {
      return description;
    }
    return this.images.length > 1 ? `${this.objectName} ${this.activeIndex + 1}` : this.objectName;
  }

  lastImage(): void {
    this.activeIndex = this.images.length - 1;
  }
  changeImage(index: number): void {
    this.activeIndex = index * this.itemsPerPage;
  }
onImageClick() {
  this.displayCustom = true;
}
onWheel(event: WheelEvent): void {
  event.preventDefault();
  if (event.deltaY < 0) {
    this.zoomIn();
  } else {
    this.zoomOut();
  }
}

resetZoom(): void {
  this.zoomScale = 1;
  this.zoomLevel = `scale(${this.zoomScale})`;
}

zoomIn(): void {
  this.zoomScale *= 1.1;
  this.zoomLevel = `scale(${this.zoomScale})`;
}

zoomOut(): void {
  this.zoomScale *= 0.9;
  this.zoomLevel = `scale(${this.zoomScale})`;
}

onImageLoad(index: number): void {
  this.imageLoadingStates[index] = 'loaded';
  delete this.imageErrorMessages[index];
}

onImageError(index: number, event: Event): void {
  const img = event.target as HTMLImageElement;
  const imageUrl = img.src;
  
  console.error(`Image failed to load: ${imageUrl}`);
  
  // Try to detect 415 error by making a HEAD request
  this.checkImageStatus(imageUrl, index);
}

private async checkImageStatus(imageUrl: string, index: number): Promise<void> {
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    
    if (response.status === 415) {
      this.imageLoadingStates[index] = 'unsupported';
      this.imageErrorMessages[index] = 'Unsupported image format';
    } else if (!response.ok) {
      this.imageLoadingStates[index] = 'error';
      this.imageErrorMessages[index] = `Failed to load image (${response.status})`;
    } else {
      // If HEAD request succeeds but image still failed, it might be a CORS issue
      this.imageLoadingStates[index] = 'error';
      this.imageErrorMessages[index] = 'Image could not be displayed';
    }
  } catch (error) {
    // Network error or CORS issue
    this.imageLoadingStates[index] = 'error';
    this.imageErrorMessages[index] = 'Network error loading image';
  }
}

getImageLoadingState(index: number): string {
  return this.imageLoadingStates[index] || 'loading';
}

getImageErrorMessage(index: number): string {
  return this.imageErrorMessages[index] || 'Image failed to load';
}

isImageUnsupported(index: number): boolean {
  return this.imageLoadingStates[index] === 'unsupported';
}

isImageError(index: number): boolean {
  return this.imageLoadingStates[index] === 'error' || this.imageLoadingStates[index] === 'unsupported';
}
}
